/*
 * Copyright (C) 2025 JPMorgan Chase & Company
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, is not permitted.
 */

import {ExdsButton, ExdsGrid, ExdsLayout, ExdsSpacing, ExdsText} from '@exds/core';
import React, {useContext, useEffect} from "react";
import './SmartLogOnDownloadApp.scss';
import AppContext from "../../../../AppContext";
import ButtonGroup from "../../../ButtonGroup/ButtonGroup";
import appleStoreBadge from "../../../../assets/images/Apple_AppStore_Badge.png";
import googlePlayBadge from "../../../../assets/images/Android_GooglePlay_Badge.png";
import QRCode from 'react-qr-code';
import SmartLogOnHeader from '../SmartLogOnHeader/SmartLogOnHeader';
import {useNavigate} from "react-router-dom";
import {addPageLoadEvent} from "../../../AdobeAnalytics/AdobeAnalytics";

export default function SmartLogOnDownloadApp(props) {
    const {setActiveMobileAppSetupPage, setPageName} = props;
    const {mobileDeviceContext, loadAA, sidContext} = useContext(AppContext);
    const sid = sidContext.at(0);
    const [mobileDevice, setMobileDevice] = mobileDeviceContext;
    const navigate = useNavigate();

  useEffect(() => {
    if(loadAA) {
      const pageName = 'Mobile App Setup | Smart Log On Download App';
      setPageName(pageName);
      addPageLoadEvent(pageName, sid);
    }
  }, [loadAA, sid]); // eslint-disable-line react-hooks/exhaustive-deps

  const cancelModalDetails = {
    title: 'Are you sure you want to cancel Mobile App Setup?',
    modalActionButton: 'Yes, I want to cancel',
    continueButton: 'No, let\'s continue'
  };

  const deviceContent = {
    'IPHONE': {
      badgeSrc: appleStoreBadge,
      qrCodeSrc: 'https://apps.apple.com/us/app/smart-log-on/id6456946128',
      linkText: 'Switch to Android',
      storeName: 'Apple Store',
      paragraphStoreName: 'the Apple Store',
      credentials: 'Apple ID'
    },
    'ANDROID': {
      badgeSrc: googlePlayBadge,
      qrCodeSrc: 'https://play.google.com/store/apps/details?id=com.acceptto.jpmc&hl=en_US',
      linkText: 'Switch to Apple iOS',
      storeName: 'Google Play',
      paragraphStoreName: 'Google Play',
      credentials: 'Google'
    }
  };

    return (
        <ExdsGrid columnSpan={8} className='step-details'>
            <SmartLogOnHeader />
            <ExdsText primitive="div" textStyle="bodyMediumHeavier" className="smartlogon-step">
                Step 1 of 3
                <br />
                <span className="smartlogon-step-title">Download and set up Smart Log On app</span>
            </ExdsText>
            <ExdsLayout flexDirection="row" gap="2rem" alignItems="flex-start" className="download-content-container">
                <ExdsLayout flexDirection="column" className="app-download">
                    <img className="os-badge" src={deviceContent[mobileDevice].badgeSrc} alt={`${deviceContent[mobileDevice].storeName} Badge`} />
                    <QRCode className="mt-1 qr-code" value={deviceContent[mobileDevice].qrCodeSrc}
                            alt={`${deviceContent[mobileDevice].storeName} QR Code`}
                            data-testid="testid-qrcode-image" />
                    <ExdsButton
                      text={deviceContent[mobileDevice].linkText}
                      variant="link"
                      className="os-link"
                      onClick={() => setMobileDevice(mobileDevice === 'IPHONE' ? 'ANDROID' : 'IPHONE')}
                      type="button"
                    />
                </ExdsLayout>
                <ExdsLayout flexDirection="column">
                    <ExdsSpacing margin={{top: "34px"}} >
                        <ExdsText primitive="div" textStyle="bodyLarge">
                            Using your mobile device's camera, scan the QR code to download Smart Log On through {deviceContent[mobileDevice].paragraphStoreName}.
                        </ExdsText>
                    </ExdsSpacing>
                </ExdsLayout>
            </ExdsLayout>
            <ButtonGroup
                advanceButtonContent='Continue'
                advanceButtonAction={() => setActiveMobileAppSetupPage('pairDevice')}
                modalButtonContent='Cancel'
                modalButtonType='button'
                modalDetails={cancelModalDetails}
                modalAction={() => navigate('/finish')}
                backButtonContent='Back'
                backButtonAction={() => setActiveMobileAppSetupPage('smartLogOn')}
            />
        </ExdsGrid>
    );
};
